import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useScriptCreation, meditationParts } from '../../contexts/ScriptCreationContext';
import { useCoins } from '../../contexts/UserContext';
import { useTTSQueue } from '../../hooks/useTTSQueue';
import { zenService } from '../../services/zenService';
import { scriptService } from '../../services/scriptService';
import { parseTextToSegments } from '../../utils/parseTextToSegments';
import { config } from '../../config';
import { HelpButton } from '../../components/common/HelpButton';

const Container = styled.div`
  margin: 0 auto;
  max-width: 900px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid #444;
  color: #cccccc;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;

  &:hover { border-color: #007bff; color: #ffffff; }
`;

const ScriptTitle = styled.h2`
  color: #ffffff;
  margin: 0 0 6px 0;
`;

const ScriptDescription = styled.p`
  color: #aaaaaa;
  margin: 0 0 25px 0;
  font-size: 15px;
`;

const PartSection = styled.div`
  background-color: #2a2a2a;
  border-radius: 12px;
  padding: 22px;
  margin-bottom: 15px;
`;

const PartTitle = styled.h4`
  color: #7bb8ff;
  margin: 0 0 12px 0;
  font-size: 15px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PartText = styled.div`
  color: #dddddd;
  font-size: 15px;
  line-height: 1.7;
  white-space: pre-wrap;
`;

const SelfGuidedNote = styled.div`
  color: #888888;
  font-style: italic;
  font-size: 14px;
`;

const ActionCard = styled.div`
  background-color: #1f2a3a;
  border: 1px solid #2a4a7a;
  border-radius: 12px;
  padding: 22px;
  margin-bottom: 15px;
`;

const ActionTitle = styled.h4`
  color: #ffffff;
  margin: 0 0 12px 0;
  font-size: 16px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  min-width: 200px;

  &:focus { outline: none; border-color: #007bff; }
`;

const PrimaryButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 12px 22px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { background-color: #0056b3; }
  &:disabled { background-color: #666; cursor: not-allowed; }
`;

const ZenActionButton = styled(PrimaryButton)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  &:hover:not(:disabled) { background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%); }
  &:disabled { background: #666; }
`;

const SecondaryLink = styled.button`
  background: none;
  border: none;
  color: #7bb8ff;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover:not(:disabled) { color: #ffffff; }
  &:disabled { color: #666; cursor: not-allowed; }
`;

const EditTextArea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 12px;
  border: 1px solid #444;
  border-radius: 8px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 12px;

  &:focus { outline: none; border-color: #007bff; }
  &::placeholder { color: #888; }
`;

const ProgressContainer = styled.div`
  margin-top: 15px;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 10px;
  background-color: #1a1a1a;
  border-radius: 5px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $percent: number }>`
  width: ${props => props.$percent}%;
  height: 100%;
  background: linear-gradient(90deg, #007bff, #667eea);
  transition: width 0.4s ease;
`;

const ProgressLabel = styled.div`
  color: #cccccc;
  font-size: 13px;
  margin-top: 6px;
`;

const AudioPlayer = styled.audio`
  width: 100%;
  margin-top: 10px;
`;

const ErrorMessage = styled.div`
  background-color: rgba(220, 53, 69, 0.15);
  border: 1px solid #dc3545;
  color: #ff8b7d;
  border-radius: 8px;
  padding: 12px 15px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const CostNote = styled.span`
  color: #ffd700;
  font-size: 13px;
`;

const MixOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  z-index: 1000;
  color: #ffffff;
  font-size: 18px;
  text-align: center;
  padding: 20px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #444;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin { to { transform: rotate(360deg); } }
`;

const helpContent = `
<div class="alert info">
Review your AI-written meditation.
</br></br>
<ul>
<li>Want changes? Describe them in plain language and the AI will revise the whole script (30 coins).</li>
<li>When you're happy, generate the speech (cost = 1 coin per character).</li>
<li>After speech is ready you can listen, then continue to AI audio mixing or fine-tune manually.</li>
</ul>
</div>
`;

const ZenPreview: React.FC = () => {
  const navigate = useNavigate();
  const {
    selectedParts,
    customPartNames,
    contentData,
    setContentData,
    editingContent,
    setEditingContent,
    zenSettings,
    setZenSettings,
    availableVoices,
    selectedVoice,
    selectedVoiceData,
    handleVoiceChange,
    loadVoices,
    playSampleAudio,
    advancedSettings,
    scriptName,
    setScriptName,
    publishDescription,
    setPublishDescription,
    publishImagePrompt,
    setPublishImagePrompt,
    saveScript,
    scriptDbId,
    partDbIds,
    segmentsToText,
    markSegmentCompleted,
    markSegmentError,
    finalizeScriptAudio,
    isFinalizingAudio,
    scriptAudioFile,
    setHasAccessedSpeech,
    loadAudioMix
  } = useScriptCreation();
  const { totalCoins, refreshCoins } = useCoins();

  const [error, setError] = useState<string | null>(null);
  const [editInstruction, setEditInstruction] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isQueueing, setIsQueueing] = useState(false);
  const [speechStarted, setSpeechStarted] = useState(false);
  const [isMixing, setIsMixing] = useState(false);
  const [pendingSave, setPendingSave] = useState<null | 'after-edit' | 'after-speech' | 'manual-write' | 'manual-speech' | 'manual-audio'>(null);
  const finalizeTriggeredRef = useRef(false);

  // Always-current zenSettings for async effects (avoids stale closures)
  const zenSettingsRef = useRef(zenSettings);
  useEffect(() => { zenSettingsRef.current = zenSettings; }, [zenSettings]);

  useEffect(() => {
    loadVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TTS queue with the standard completion wiring
  const onSegmentCompleted = useCallback((segmentId: string, partId: string, audioFile: string, duration?: number, ttsCacheId?: string) => {
    markSegmentCompleted(partId, segmentId, audioFile, ttsCacheId);
    refreshCoins();
  }, [markSegmentCompleted, refreshCoins]);

  const onSegmentError = useCallback((segmentId: string, partId: string, segmentError: string) => {
    markSegmentError(partId, segmentId, segmentError);
  }, [markSegmentError]);

  const { queueBatch, globalQueueLength, isConnected } = useTTSQueue({ onSegmentCompleted, onSegmentError });

  // Speech generation progress derived from contentData
  const { totalTextSegments, completedTextSegments, errorTextSegments, speechCost } = useMemo(() => {
    let total = 0, completed = 0, errors = 0, cost = 0;
    selectedParts.forEach(partId => {
      (contentData[partId] || []).forEach(seg => {
        if (seg.type === 'text') {
          total++;
          cost += seg.content.length;
          if (seg.generationStatus === 'completed' || seg.generatedAudioFile) completed++;
          if (seg.generationStatus === 'error') errors++;
        }
      });
    });
    return { totalTextSegments: total, completedTextSegments: completed, errorTextSegments: errors, speechCost: cost };
  }, [selectedParts, contentData]);

  const allSpeechComplete = totalTextSegments > 0 && completedTextSegments === totalTextSegments;
  const speechReady = zenSettings?.stage === 'speech_generated' || zenSettings?.stage === 'mix_generated';

  const getPartTitle = (partId: string): string => {
    return customPartNames[partId] || meditationParts.find(p => p.id === partId)?.title || partId;
  };

  const isSelfGuidedPart = (partId: string): boolean => {
    if (partId === 'selfguided') return true;
    const segments = contentData[partId] || [];
    return segments.length > 0 && segments.every(seg => seg.type === 'pause');
  };

  // ============ Generate Speech ============
  const handleGenerateSpeech = async () => {
    setError(null);
    if (!selectedVoiceData) {
      setError('Please select a voice first.');
      return;
    }
    if (totalCoins < speechCost) {
      setError(`Not enough coins. Speech generation costs ${speechCost} coins; you have ${totalCoins}.`);
      return;
    }

    setIsQueueing(true);
    try {
      // Persist any changes (voice etc.) before queueing
      await saveScript();

      const batchSegments: any[] = [];
      selectedParts.forEach(partId => {
        (contentData[partId] || []).forEach(seg => {
          if (seg.type === 'text' && !(seg.generationStatus === 'completed' || seg.generatedAudioFile)) {
            batchSegments.push({
              segmentId: seg.id,
              scriptId: scriptDbId,
              partId: partDbIds[partId] || partId,
              text: seg.content,
              speed: seg.speechSpeed || advancedSettings.speechSpeed
            });
          }
        });
      });

      if (batchSegments.length === 0) {
        setSpeechStarted(true);
        return;
      }

      finalizeTriggeredRef.current = false;
      await queueBatch(batchSegments, selectedVoiceData.voiceId);
      setSpeechStarted(true);
    } catch (e: any) {
      console.error('Speech generation failed:', e);
      setError(e?.message || 'Failed to start speech generation.');
    } finally {
      setIsQueueing(false);
    }
  };

  // When every segment is done, finalize the speech audio once
  useEffect(() => {
    if (!speechStarted || !allSpeechComplete || finalizeTriggeredRef.current || isFinalizingAudio) return;
    finalizeTriggeredRef.current = true;
    (async () => {
      // finalizeScriptAudio reports failure by returning null (it never throws)
      const audioFile = await finalizeScriptAudio();
      if (audioFile) {
        const currentZen = zenSettingsRef.current;
        if (currentZen) {
          setZenSettings({ ...currentZen, stage: 'speech_generated' });
        }
        setPendingSave('after-speech');
      } else {
        console.error('Failed to finalize speech audio');
        setError('Speech was generated but could not be finalized. Press Generate Speech to try again.');
        finalizeTriggeredRef.current = false;
        setSpeechStarted(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speechStarted, allSpeechComplete, isFinalizingAudio]);

  // ============ Edit request ============
  const handleEditRequest = async () => {
    setError(null);
    if (!editInstruction.trim()) return;
    if (generationBusy) return; // defense in depth — the button is also disabled
    if (totalCoins < 30) {
      setError(`Not enough coins. Edit requests cost 30 coins; you have ${totalCoins}.`);
      return;
    }
    if (speechReady || completedTextSegments > 0) {
      const confirmed = window.confirm(
        'Changing the script will require regenerating the speech for the updated meditation. Continue?'
      );
      if (!confirmed) return;
    }

    setIsEditing(true);
    try {
      const parts = selectedParts.map(partId => ({
        partId,
        partTitle: getPartTitle(partId),
        text: editingContent[partId] || segmentsToText(contentData[partId] || [])
      }));

      const result = await zenService.editScript({
        goal: zenSettings?.goal || '',
        instruction: editInstruction.trim(),
        title: scriptName,
        description: publishDescription,
        image_prompt: publishImagePrompt,
        parts
      });

      setScriptName(result.title);
      setPublishDescription(result.description);
      setPublishImagePrompt(result.image_prompt);

      const newContentData = { ...contentData };
      const newEditingContent = { ...editingContent };
      result.parts.forEach(({ partId, text }) => {
        const segments = parseTextToSegments(text, partId, 'sentence', advancedSettings);
        newContentData[partId] = segments;
        newEditingContent[partId] = segmentsToText(segments);
      });
      setContentData(newContentData);
      setEditingContent(newEditingContent);
      if (zenSettingsRef.current) {
        // Fresh script text invalidates any generated speech
        setZenSettings({ ...zenSettingsRef.current, stage: 'script_generated' });
      }
      setSpeechStarted(false);
      finalizeTriggeredRef.current = false;
      setEditInstruction('');
      await refreshCoins();
      setPendingSave('after-edit');
    } catch (e: any) {
      console.error('Edit request failed:', e);
      setError(e?.response?.data?.error || 'Failed to revise the script. Please try again.');
    } finally {
      setIsEditing(false);
    }
  };

  // ============ Zen mix ============
  const handleZenMix = async () => {
    setError(null);
    if (!scriptDbId) return;
    if (totalCoins < 100) {
      setError(`Not enough coins. AI audio mixing costs 100 coins; you have ${totalCoins}.`);
      return;
    }
    setIsMixing(true);
    try {
      const result = await zenService.generateMix(scriptDbId);
      await loadAudioMix();
      if (zenSettingsRef.current) {
        setZenSettings({ ...zenSettingsRef.current, stage: 'mix_generated', mixSummary: result.summary });
      }
      await refreshCoins();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('../zen-summary');
    } catch (e: any) {
      console.error('Zen mix failed:', e);
      setError(e?.response?.data?.error || 'Failed to generate the audio mix. Please try again.');
    } finally {
      setIsMixing(false);
    }
  };

  // ============ Manual mode exits ============
  const handleSwitchToManual = (destination: 'manual-write' | 'manual-speech' | 'manual-audio') => {
    // Leaving zen mode entirely — the manual flow takes over from here
    setZenSettings(null);
    if (destination !== 'manual-write') {
      setHasAccessedSpeech(true);
    }
    setPendingSave(destination);
  };

  // Deferred saves so saveScript reads fresh state after the triggering updates
  useEffect(() => {
    if (!pendingSave) return;
    const action = pendingSave;
    setPendingSave(null);
    (async () => {
      const saved = await saveScript();
      if (!saved) {
        console.error('Save failed after', action);
        setError('Your changes could not be saved. Use the Save button in the header to retry.');
      } else if (action === 'after-edit' && scriptDbId) {
        try {
          await scriptService.updateScript(scriptDbId, {
            script_description: publishDescription,
            art_prompt: publishImagePrompt
          } as any);
        } catch { /* non-fatal */ }
      }
      if (action === 'manual-write') navigate('../write');
      if (action === 'manual-speech') navigate('../speech');
      if (action === 'manual-audio') navigate('../audio');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSave]);

  if (!zenSettings && !pendingSave) {
    // Not a zen script (or user already switched to manual) — nothing to show here
    return (
      <Container>
        <ErrorMessage>This page is only available for Intelligent Zen Mode meditations.</ErrorMessage>
        <BackButton onClick={() => navigate('../plan')}>Back to Plan</BackButton>
      </Container>
    );
  }

  const progressPercent = totalTextSegments > 0 ? Math.round((completedTextSegments / totalTextSegments) * 100) : 0;
  // Errored segments are terminal — re-enable the button so the user can retry
  const speechInProgress = speechStarted && !allSpeechComplete && errorTextSegments === 0;
  const generationBusy = isQueueing || speechInProgress || isFinalizingAudio;

  return (
    <Container>
      <TopBar>
        <BackButton onClick={() => navigate('../zen')}>← Back to Options</BackButton>
      </TopBar>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ScriptTitle>{scriptName || 'Your Meditation'}</ScriptTitle>
      {publishDescription && <ScriptDescription>{publishDescription}</ScriptDescription>}

      {selectedParts.map(partId => (
        <PartSection key={partId}>
          <PartTitle>{getPartTitle(partId)}</PartTitle>
          {isSelfGuidedPart(partId) ? (
            <SelfGuidedNote>
              Self-guided silence{(() => {
                const pause = (contentData[partId] || []).find(seg => seg.type === 'pause');
                const seconds = pause?.pauseTime ? parseFloat(pause.pauseTime) : 0;
                return seconds ? ` — ${Math.round(seconds / 60 * 10) / 10} minutes` : '';
              })()}
            </SelfGuidedNote>
          ) : (
            <PartText>{editingContent[partId] || segmentsToText(contentData[partId] || [])}</PartText>
          )}
        </PartSection>
      ))}

      {/* Generate speech */}
      <ActionCard>
        <ActionTitle>Generate Speech</ActionTitle>
        <Row>
          <Select
            value={selectedVoice === 'default' ? '' : selectedVoice}
            onChange={e => {
              handleVoiceChange(e.target.value);
              const voice = availableVoices.find(v => v.id === e.target.value);
              if (voice?.sampleAudio) playSampleAudio(voice.id);
            }}
            disabled={speechInProgress || isQueueing}
          >
            <option value="">Select a voice…</option>
            {availableVoices.map(voice => (
              <option key={voice.id} value={voice.id}>{voice.name}</option>
            ))}
          </Select>
          <PrimaryButton
            onClick={handleGenerateSpeech}
            disabled={isQueueing || speechInProgress || isFinalizingAudio || (speechReady && allSpeechComplete)}
          >
            {isQueueing ? 'Queueing…'
              : speechInProgress ? 'Generating…'
              : isFinalizingAudio ? 'Finalizing…'
              : speechReady && allSpeechComplete ? '✓ Speech Generated'
              : 'Generate Speech'}
          </PrimaryButton>
          <CostNote>{speechCost} coins</CostNote>
        </Row>

        {(speechInProgress || isFinalizingAudio || (speechStarted && allSpeechComplete)) && (
          <ProgressContainer>
            <ProgressBarTrack>
              <ProgressBarFill $percent={isFinalizingAudio ? 100 : progressPercent} />
            </ProgressBarTrack>
            <ProgressLabel>
              {isFinalizingAudio
                ? 'Combining audio…'
                : `${completedTextSegments} / ${totalTextSegments} segments complete`}
              {errorTextSegments > 0 && ` — ${errorTextSegments} failed (retry by pressing Generate Speech again)`}
              {speechInProgress && globalQueueLength > 1 && ` — queue: ${globalQueueLength}`}
              {speechInProgress && !isConnected && ' — reconnecting…'}
            </ProgressLabel>
          </ProgressContainer>
        )}

        {speechReady && scriptAudioFile && (
          <AudioPlayer controls src={`${config.apiUrl}${scriptAudioFile}`} />
        )}
      </ActionCard>

      {/* Edit request */}
      <ActionCard>
        <ActionTitle>Want to make a change? Just say so:</ActionTitle>
        <EditTextArea
          value={editInstruction}
          onChange={e => setEditInstruction(e.target.value)}
          placeholder='e.g. "Remove flowing river reference, and give it a more coastal/ocean feel"'
          maxLength={500}
          disabled={isEditing || generationBusy}
        />
        <Row>
          <PrimaryButton onClick={handleEditRequest} disabled={isEditing || generationBusy || !editInstruction.trim()}>
            {isEditing ? 'Revising…' : generationBusy ? 'Wait for speech to finish…' : 'Request Changes'}
          </PrimaryButton>
          <CostNote>30 coins</CostNote>
        </Row>
      </ActionCard>

      {/* Continue / exits */}
      {speechReady && (
        <ActionCard>
          <ActionTitle>Your speech is ready</ActionTitle>
          <Row>
            <ZenActionButton onClick={handleZenMix} disabled={isMixing || !scriptDbId}>
              {isMixing ? 'Mixing…' : '✨ Continue with Intelligent Zen Audio Mixing'}
            </ZenActionButton>
            <CostNote>100 coins</CostNote>
          </Row>
          <Row style={{ marginTop: 14 }}>
            <SecondaryLink onClick={() => handleSwitchToManual('manual-speech')} disabled={generationBusy || isMixing}>
              Switch to manual to fine-tune speech
            </SecondaryLink>
            <SecondaryLink onClick={() => handleSwitchToManual('manual-audio')} disabled={generationBusy || isMixing}>
              Switch to manual audio mixing
            </SecondaryLink>
          </Row>
        </ActionCard>
      )}

      <Row style={{ margin: '10px 0 40px' }}>
        <SecondaryLink onClick={() => handleSwitchToManual('manual-write')} disabled={generationBusy || isMixing}>
          If you want to make manual edits, switch to Manual Mode
        </SecondaryLink>
      </Row>

      {isMixing && (
        <MixOverlay>
          <Spinner />
          <div>Designing your audio mix…<br /><small>The AI is exploring the audio library and building your soundscape.</small></div>
        </MixOverlay>
      )}

      <HelpButton
        title="Intelligent Zen Mode - Preview"
        content={helpContent}
        position="fixed-bottom-right"
      />
    </Container>
  );
};

export default ZenPreview;
